import React, { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { useDispatch } from 'react-redux';
import { Segment, Card, Message } from 'semantic-ui-react';
import { getContent } from '@plone/volto/actions';
import config from '@plone/volto/registry';

const View = (props) => {
  const { className, data } = props;
  const dispatch = useDispatch();
  const selected = data?.selected_file;

  const apiBase = config.settings?.apiPath;
  // Resolve target path similar to Edit
  const targetPath = useMemo(() => {
    return (data?.target_path || '').trim();
  }, [data?.target_path]);
  const detailUrl = useMemo(() => {
    const href = selected?.['@id'];
    if (!href || !apiBase) return null;
    // Use the backend service to read JSON content of the selected file
    const q = new URLSearchParams({ file_url: href }).toString();
    return `${apiBase}/++api++/@applist-upload?${q}`;
  }, [selected, apiBase]);

  const [apps, setApps] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noFile, setNoFile] = useState(false);
  const [listingLoading, setListingLoading] = useState(false);
  const last404Ref = useRef(null);

  useEffect(() => {
    let aborted = false;
    async function load() {
      if (!detailUrl) {
        setApps(null);
        setError(null);
        setLoading(false);
        return;
      }
      const href = selected?.['@id'] || null;
      // Prevent repeated 404 fetches for the same missing href
      if (href && last404Ref.current === href) {
        setNoFile(true);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        setNoFile(false);
        // Ensure object metadata is accessible (SSR/client); no-op if cached
        if (selected?.['@id']) {
          dispatch(
            getContent(
              selected['@id'],
              null,
              `applist-view-${encodeURIComponent(selected['@id'] || '')}`,
            ),
          );
        }
        const res = await fetch(detailUrl, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) {
          if (res.status === 404) {
            if (!aborted) {
              setNoFile(true);
              setApps(null);
            }
            // Remember this href to avoid refetch loop
            if (href) last404Ref.current = href;
          }
          throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        const value = json?.apps || null;
        if (!aborted) setApps(value);
        // Clear last 404 since we loaded successfully
        last404Ref.current = null;
      } catch (e) {
        if (!aborted) setError(e.message || String(e));
      } finally {
        if (!aborted) setLoading(false);
      }
    }
    load();
    return () => {
      aborted = true;
    };
  }, [detailUrl, dispatch, selected]);

  // When no file is selected, consult backend listing to know if folder is empty
  useEffect(() => {
    let aborted = false;
    async function loadListing() {
      if (selected || !apiBase) return;
      try {
        setListingLoading(true);
        setError(null);
        const url = `${apiBase}/++api++/@applist-upload?target_path=${encodeURIComponent(
          targetPath,
        )}`;
        const res = await fetch(url, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } catch (e) {
        if (!aborted) {
          // Do not block UI, but surface error message
          setError(e.message || String(e));
        }
      } finally {
        if (!aborted) setListingLoading(false);
      }
    }
    loadListing();
    return () => {
      aborted = true;
    };
  }, [selected, apiBase, targetPath]);

  const entries = useMemo(() => {
    if (!apps) return [];
    return Object.entries(apps).map(([id, info]) => ({
      id,
      version: info?.version,
      desc: info?.desc,
    }));
  }, [apps]);

  return (
    <Segment className={cx('block', 'applistupload', className)}>
      {(data?.title || data?.description) && (
        <div style={{ marginBottom: 12 }}>
          {data?.title && <h3 style={{ margin: '0 0 4px' }}>{data.title}</h3>}
          {data?.description && (
            <p style={{ margin: 0, color: 'rgba(0,0,0,0.6)' }}>
              {data.description}
            </p>
          )}
        </div>
      )}
      {!selected ? (
        listingLoading ? (
          <Message info content="Lade Daten..." />
        ) : null
      ) : loading ? (
        <Message info content="Lade Daten..." />
      ) : noFile ? (
        <Message info content="Keine Datei existiert." />
      ) : error ? (
        <Message negative content={error} />
      ) : (
        <>
          {entries.length === 0 ? (
            <Card.Description>Keine Daten vorhanden.</Card.Description>
          ) : (
            <p>{entries.length} Apps</p>
          )}
          {entries.length > 0 && (
            <Card.Group itemsPerRow={4} stackable doubling>
              {entries.map((item) => {
                const label = item.id
                  ? item.id.charAt(0).toUpperCase() + item.id.slice(1)
                  : '';
                return (
                  <Card key={item.id} raised={false}>
                    <Card.Content>
                      <Card.Header title={label} style={{ fontSize: '0.95em' }}>
                        {label}
                      </Card.Header>
                      <Card.Meta style={{ marginTop: 4 }}>
                        Version: {item.version || '-'}
                      </Card.Meta>
                      <Card.Description style={{ marginTop: 6 }}>
                        {item.desc && item.desc.trim().length > 0
                          ? item.desc
                          : 'Beschreibung fehlt'}
                      </Card.Description>
                    </Card.Content>
                  </Card>
                );
              })}
            </Card.Group>
          )}
        </>
      )}
    </Segment>
  );
};

export default View;
